import { ReactTFC } from "../../../../../../utils/fp/missing-ts-parts";
import { DualCheckoutVisualButton } from "./DualCheckoutVisualButton";
import { AdyenComponent } from "../../adyen/AdyenComponent";
import { css } from "@emotion/react";
import React, { useEffect, useState } from "react";
import { PaymentMethodT } from "../../data/available-payment-methods";
import { CCState } from "./types";
import { labelStyles } from "./labelStyles";
import { useAdyenEnhancedForm } from "../../adyen/use-adyen-form";
import Core from "@adyen/adyen-web/dist/types/core";
import { CheckoutPaymentMethodSelectionState_CreditCard } from "../../state/payment-methods";
import { CheckoutSelectionState } from "../../state/type";
import { getPaymentMethodsIconComponent } from "./getPaymentMethodsIconComponent";
import { OpenedPaymentMethod } from "./CheckoutPaymentMethod";
import { TranslatedValue } from "@swo/storefront";
import { T } from "../../../../../../components/T";

type CCTranslations = {
  creditCard: string | TranslatedValue;
  creditCardSave: string | TranslatedValue;
  creditCardEdit: string | TranslatedValue;
  creditCardClose: string | TranslatedValue;
  creditCardNumber: string | TranslatedValue;
  creditCardExpiryDate: string | TranslatedValue;
  creditCardSecurityCode: string | TranslatedValue;
};

type CreditCardPaymentMethodProps = {
  paymentOption: {
    tag: "credit-card";
    brands: Array<string>;
  };
  className: string;
  disabled: boolean;
  openedPaymentMethod: OpenedPaymentMethod;
  setOpenedPaymentMethod: React.Dispatch<OpenedPaymentMethod>;
  translations: CCTranslations;
  adyenInstance: Core;
};

export const CreditCardPaymentMethod: ReactTFC<CreditCardPaymentMethodProps> = props => {
  const { paymentOption, openedPaymentMethod, setOpenedPaymentMethod, adyenInstance } = props;
  const [previousData, setPreviousData] = useState<CheckoutPaymentMethodSelectionState_CreditCard | null>(null);

  const formState = useAdyenEnhancedForm(adyenInstance, {
    expiryDate: props.translations.creditCardExpiryDate,
    creditCardNumber: props.translations.creditCardNumber,
    securityCode: props.translations.creditCardSecurityCode,
  });

  if (
    openedPaymentMethod?.tag === "credit-card" &&
    openedPaymentMethod.screen.tag === "input-card-data" &&
    previousData
  ) {
    setOpenedPaymentMethod({ tag: "credit-card", screen: { tag: "show-saved-card", data: previousData } });
  }

  const screen: CCState =
    openedPaymentMethod?.tag === "credit-card" ? openedPaymentMethod.screen : { tag: "input-card-data" };

  const PaymentMethodsIconComponent = getPaymentMethodsIconComponent(paymentOption.brands, screen, openedPaymentMethod);

  function onEditCardDataRequested() {
    setPreviousData(null);
    setOpenedPaymentMethod({ tag: "credit-card", screen: { tag: "input-card-data" } });
  }

  function onClick() {
    if (openedPaymentMethod?.tag === "credit-card") {
      return undefined;
    } else {
      if (previousData) {
        setOpenedPaymentMethod({ tag: "credit-card", screen: { tag: "show-saved-card", data: previousData } });
      } else {
        setOpenedPaymentMethod({ tag: "credit-card", screen: { tag: "input-card-data" } });
      }
    }
  }

  return (
    <DualCheckoutVisualButton
      key={paymentOption.tag}
      disabled={props.disabled}
      isSelected={openedPaymentMethod?.tag === "credit-card"}
      onClick={onClick}
      Extension={
        <AdyenComponent
          onSaved={data => {
            setOpenedPaymentMethod({ tag: "credit-card", screen: { tag: "show-saved-card", data } });
            setPreviousData(data);
          }}
          screen={screen}
          translations={props.translations}
          formState={formState}
          onEditCardDataRequested={onEditCardDataRequested}
        />
      }
      isOpened={props.openedPaymentMethod?.tag === "credit-card"}
      className={props.className}
    >
      <span css={labelStyles}>
        <T>{props.translations.creditCard}</T>
      </span>

      <div
        css={css`
          display: flex;
          gap: 4px;
        `}
      >
        {PaymentMethodsIconComponent}
      </div>
    </DualCheckoutVisualButton>
  );
};
